'use client';

import { useAutoNext } from '@/hooks/useAutoNext';
import { CourseLesson } from '@/types/course';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Simplified course type for learning path navigation
interface LearningPathCourse {
  url: string;
  title?: string;
  uid?: string;
  chapters?: {
    lessons?: {
      lessons?: CourseLesson[];
    };
  }[];
}

interface LessonNavigationProps {
  lessons: CourseLesson[];
  currentLessonUrl: string;
  courseId: string;
  courseEntryUid: string;
  currentLessonEntryUid?: string;
  learningPath?: string;
  learningPathCourses?: LearningPathCourse[];
  /** CMS `is_assessment` — controls last-step copy and assessment routing */
  learningPathHasAssessment?: boolean;
  showPrevious?: boolean;
}

export default function LessonNavigation({
  lessons,
  currentLessonUrl,
  courseId,
  courseEntryUid,
  currentLessonEntryUid,
  learningPath,
  learningPathCourses,
  learningPathHasAssessment = false,
  showPrevious = true,
}: LessonNavigationProps) {
  const {
    goToNextLesson,
    goToPreviousLesson,
    isLastLesson,
    isLastCourseInPath,
    nextLesson,
    previousLesson,
    hasNext,
    hasPrevious,
  } = useAutoNext({
    lessons,
    currentLessonUrl,
    courseId,
    courseEntryUid,
    currentLessonEntryUid,
    learningPath,
    learningPathCourses,
    learningPathHasAssessment,
  });

  return (
    <div className="mt-8 flex items-center justify-between gap-4 border-t border-[var(--Borders-Medium)] pt-6">
      {/* Previous Button */}
      <div className="flex flex-1 flex-col items-start">
        {showPrevious && hasPrevious && (
          <button
            onClick={goToPreviousLesson}
            className="group flex h-[52px] w-full max-w-[160px] cursor-pointer items-center overflow-hidden rounded-lg transition-opacity hover:opacity-90 sm:max-w-[200px] md:max-w-[240px]"
            style={{
              background: 'linear-gradient(180deg, var(--Surfaces-Overlay-Sheen-0) 0%, var(--Surfaces-Overlay-Sheen-50) 100%), var(--Surfaces-Surface-3)',
            }}
          >
            {/* Previous Arrow */}
            <div
              className="flex h-full items-center self-stretch px-1"
              style={{
                background: 'linear-gradient(180deg, var(--Surfaces-Extended-Amethyst-Transparent-Soft) 0%, var(--Surfaces-Extended-Amethyst-Transparent-Silence) 100%)',
              }}
            >
              <ChevronLeft className="h-4 w-4 flex-shrink-0 text-[var(--Icons-Light)]" />
            </div>

            {/* Content */}
            <div className="flex min-w-0 flex-1 flex-col justify-center px-4 py-2 text-left">
              <span className="w-full truncate font-inter text-sm font-semibold leading-[120%] text-[var(--Text-Default-Medium)]">
                Previous
              </span>
              <span className="w-full truncate font-inter text-xs font-normal leading-[140%] text-[var(--Text-Default-Light)]">
                {previousLesson?.title}
              </span>
            </div>
          </button>
        )}
      </div>

      {/* Next/Complete Button */}
      <div className="flex flex-1 flex-col items-end">
        <button
          onClick={goToNextLesson}
          className="group flex h-[52px] w-full max-w-[160px] cursor-pointer items-center overflow-hidden rounded-lg transition-opacity hover:opacity-90 sm:max-w-[200px] md:max-w-[240px]"
          style={{
            background: 'linear-gradient(180deg, var(--Surfaces-Overlay-Sheen-0) 0%, var(--Surfaces-Overlay-Sheen-50) 100%), var(--Surfaces-Surface-3)',
          }}
        >
          {/* Content */}
          <div className="flex min-w-0 flex-1 flex-col justify-center px-4 py-2 text-left">
            <span className="w-full truncate font-inter text-sm font-semibold leading-[120%] text-[var(--Text-Default-Medium)]">
              {!isLastLesson ? 'Next' : learningPath && !isLastCourseInPath ? 'Next Course' : 'Complete'}
            </span>
            <span className="w-full truncate font-inter text-xs font-normal leading-[140%] text-[var(--Text-Default-Light)]">
              {!isLastLesson
                ? nextLesson?.title
                : learningPath && isLastCourseInPath
                  ? learningPathHasAssessment
                    ? 'Go to Assessment'
                    : 'Back to learning path'
                  : learningPath && !isLastCourseInPath
                    ? 'Continue Learning Path'
                    : 'Complete Course'}
            </span>
          </div>

          {/* Next Arrow */}
          <div
            className="flex h-full items-center self-stretch px-1"
            style={{
              background: 'linear-gradient(180deg, var(--Surfaces-Extended-Amethyst-Transparent-Soft) 0%, var(--Surfaces-Extended-Amethyst-Transparent-Silence) 100%)',
            }}
          >
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-[var(--Icons-Light)]" />
          </div>
        </button>
      </div>
    </div>
  );
}
